import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { translate } from '@optimusgps/optimus-intl';
import { message, Skeleton, Table, Tag, Typography } from 'antd';
import AdvancedFilter from '@/OptimusRoutes/components/filter/AdvancedFilter';
import { SHORT_DATE_TIME_WITH_TIMEZONE_FORMAT } from '@/OptimusRoutes/constants';
import { useAppSelector } from '@/OptimusRoutes/hooks/redux.hooks';
import useAdvancedFilter from '@/OptimusRoutes/hooks/useAdvancedFilter.hook';
import useFetchData from '@/OptimusRoutes/hooks/useFetchData.hook';
import { EntityFilter, Filter, Where } from '@/OptimusRoutes/interfaces';
import { AppliedFilter, FilterData, FilterDataSelector } from '@/OptimusRoutes/types/filterComponent.types';
import { RootState } from '@/store/configureStore';
import { dateToUtc, formatDate, utcStartAndEndOfDay } from '@/utilities/dateUtils';
import { slashToHyphenFormat } from '@/utilities/util';
import { formatEmailTypeLabel } from '@/utilities/utilities';
import { GetEmailLogsResponseDto, GetEmailTypesResponseDto, OutboundEmailLog } from '../diagnostics.dto';
import DiagnosticsService from '../diagnostics.service';
import { getStatusTagColor } from './emailLogs.utils';
import { EventsModal, SelectedEmailLog } from './EventsModal';

const { Link } = Typography;

const STATUS_OPTIONS: FilterDataSelector[] = [
	{ key: 1, value: 'SENT', label: 'Sent' },
	{ key: 2, value: 'DELIVERED', label: 'Delivered' },
	{ key: 3, value: 'OPENED', label: 'Opened' },
	{ key: 4, value: 'BOUNCED', label: 'Bounced' },
	{ key: 5, value: 'COMPLAINT', label: 'Complaint' },
	{ key: 6, value: 'FAILED', label: 'Failed' },
	{ key: 7, value: 'PROCESSED', label: 'Processed' },
	{ key: 8, value: 'REJECTED', label: 'Rejected' },
];

const PROVIDER_OPTIONS: FilterDataSelector[] = [
	{ key: 1, value: 'SES', label: 'AWS SES' },
	{ key: 2, value: 'Sendgrid', label: 'Sendgrid' },
];

type SearchFilterValues = Pick<
	Partial<OutboundEmailLog>,
	| 'recipientEmail'
	| 'providerMessageId'
	| 'subject'
	| 'mappedStatus'
	| 'fromEmail'
	| 'emailType'
	| 'providerType'
	| 'createdAt'
	| 'eventAt'
> & {
	fromDate?: string;
	toDate?: string;
};

enum FilterOptions {
	RECIPIENT_EMAIL = 1,
	MESSAGE_ID = 2,
	SUBJECT = 3,
	STATUS = 4,
	FROM = 5,
	EMAIL_TYPE = 6,
	PROVIDER = 7,
	PROCESSED_AT = 8,
	UPDATED_AT = 9,
	FROM_DATE = 10,
	TO_DATE = 11,
}

export const EmailLogsPage = () => {
	const intl = useIntl();
	const systemConfig = useAppSelector((state: RootState) => state.auth.user.unitSystemConfig);
	const [selectedEmailLog, setSelectedEmailLog] = useState<SelectedEmailLog | null>(null);
	const [emailTypeOptions, setEmailTypeOptions] = useState<FilterDataSelector[]>([]);
	const [isEmailTypesLoading, setIsEmailTypesLoading] = useState(false);

	const dateFormat = slashToHyphenFormat(systemConfig);

	const formatSearchFilter = (value: SearchFilterValues | string): EntityFilter<SearchFilterValues> => {
		if (typeof value === 'string') {
			return {};
		}

		const where: Where<SearchFilterValues> = {};

		if (value.recipientEmail) {
			where.recipientEmail = { value: value.recipientEmail, op: 'like' };
		}

		if (value.providerMessageId) {
			where.providerMessageId = { value: value.providerMessageId, op: 'like' };
		}

		if (value.subject) {
			where.subject = { value: value.subject, op: 'like' };
		}

		if (value.mappedStatus) {
			where.mappedStatus = value.mappedStatus;
		}

		if (value.fromEmail) {
			where.fromEmail = { value: value.fromEmail, op: 'like' };
		}

		if (value.emailType) {
			where.emailType = value.emailType;
		}

		if (value.providerType) {
			where.providerType = value.providerType;
		}

		if (value.createdAt) {
			const { startOfDate, endOfDate } = utcStartAndEndOfDay(value.createdAt, dateFormat);
			where.createdAt = { value: { from: startOfDate, to: endOfDate }, op: 'between' };
		} else if (value.fromDate) {
			if (value.toDate && new Date(value.toDate) < new Date(value.fromDate)) {
				message.error(intl.formatMessage(translate('endDateBeforeStartDate')));
				return null as unknown as EntityFilter<SearchFilterValues>;
			}

			const { startOfDate: from } = utcStartAndEndOfDay(value.fromDate, dateFormat);
			const to = value.toDate ? utcStartAndEndOfDay(value.toDate, dateFormat).endOfDate : dateToUtc(new Date());

			where.createdAt = { value: { from, to }, op: 'between' };
		}

		if (value.eventAt) {
			const { startOfDate, endOfDate } = utcStartAndEndOfDay(value.eventAt, dateFormat);
			where.eventAt = { value: { from: startOfDate, to: endOfDate }, op: 'between' };
		}

		return { where, order: { createdAt: 'DESC' } };
	};

	const { data, loading, total, updatePagination, pagination, search } = useFetchData<
		GetEmailLogsResponseDto,
		SearchFilterValues
	>({
		service: {
			get: (filter: Filter<OutboundEmailLog>) => DiagnosticsService.getEmailLogs(filter),
		},
		formatSearchFilter,
	});

	const { clearFilters, setAppliedFilters, fetchFilteredData } = useAdvancedFilter({ search });

	const onFilterChange = (filtersValues: AppliedFilter[]): void => {
		setAppliedFilters(filtersValues);
	};

	const onClearFilters = (): void => {
		clearFilters();
	};

	const handleOpenModal = (record: GetEmailLogsResponseDto) => {
		setSelectedEmailLog({
			providerMessageId: record.providerMessageId,
			recipientEmail: record.recipientEmail,
			subject: record.subject,
			fromEmail: record.fromEmail,
			emailStatus: record.mappedStatus,
			emailType: record.emailType,
		});
	};

	const handleCloseModal = () => {
		setSelectedEmailLog(null);
	};

	const handleTableChange = (paginationConfig: { current?: number; pageSize?: number }) => {
		updatePagination({
			page: paginationConfig.current || 1,
			size: paginationConfig.pageSize || 10,
		});
	};

	const columns = [
		{
			title: intl.formatMessage(translate('processedOn')),
			dataIndex: 'createdAt',
			key: 'createdAt',
			render: (value: Date) => formatDate(SHORT_DATE_TIME_WITH_TIMEZONE_FORMAT, value, true),
		},
		{
			title: intl.formatMessage(translate('messageId')),
			dataIndex: 'providerMessageId',
			key: 'providerMessageId',
			render: (value: string, record: GetEmailLogsResponseDto) => (
				<Link onClick={() => handleOpenModal(record)}>{value}</Link>
			),
		},
		{
			title: intl.formatMessage(translate('provider')),
			dataIndex: 'providerType',
			key: 'providerType',
		},
		{
			title: intl.formatMessage(translate('updatedOn')),
			dataIndex: 'eventAt',
			key: 'eventAt',
			render: (value: Date) => formatDate(SHORT_DATE_TIME_WITH_TIMEZONE_FORMAT, value, true),
		},
		{
			title: intl.formatMessage(translate('status')),
			dataIndex: 'mappedStatus',
			key: 'mappedStatus',
			align: 'center' as const,
			render: (value: string) => (
				<Tag className="email-logs__status-tag" color={getStatusTagColor(value)}>
					{intl.formatMessage(translate(value?.toLowerCase()))}
				</Tag>
			),
		},
		{
			title: intl.formatMessage(translate('emailType')),
			dataIndex: 'emailType',
			key: 'emailType',
			render: (value: string) => formatEmailTypeLabel(value),
		},
		{
			title: intl.formatMessage(translate('subject')),
			dataIndex: 'subject',
			key: 'subject',
		},
		{
			title: intl.formatMessage(translate('senderEmail')),
			dataIndex: 'fromEmail',
			key: 'fromEmail',
		},
		{
			title: intl.formatMessage(translate('recipientEmail')),
			dataIndex: 'recipientEmail',
			key: 'recipientEmail',
		},
	];

	const filters: FilterData[] = [
		{
			id: FilterOptions.RECIPIENT_EMAIL,
			type: 'TEXT',
			filterwhere: 'recipientEmail',
			intlLabelCode: 'recipientEmail',
			placeholder: intl.formatMessage(translate('recipientEmail')),
		},
		{
			id: FilterOptions.MESSAGE_ID,
			type: 'TEXT',
			filterwhere: 'providerMessageId',
			intlLabelCode: 'messageId',
			placeholder: intl.formatMessage(translate('messageId')),
		},
		{
			id: FilterOptions.SUBJECT,
			type: 'TEXT',
			filterwhere: 'subject',
			intlLabelCode: 'subject',
			placeholder: intl.formatMessage(translate('subject')),
		},
		{
			id: FilterOptions.STATUS,
			type: 'DROPDOWN',
			filterwhere: 'mappedStatus',
			intlLabelCode: 'status',
			placeholder: intl.formatMessage(translate('status')),
			options: STATUS_OPTIONS,
		},
		{
			id: FilterOptions.FROM,
			type: 'TEXT',
			filterwhere: 'fromEmail',
			intlLabelCode: 'senderEmail',
			placeholder: intl.formatMessage(translate('from')),
		},
		{
			id: FilterOptions.EMAIL_TYPE,
			type: 'DROPDOWN',
			filterwhere: 'emailType',
			intlLabelCode: 'emailType',
			placeholder: intl.formatMessage(translate('emailType')),
			options: emailTypeOptions,
			showSearch: true,
			optionFilterProp: 'label',
			dropdownMatchSelectWidth: false,
		},
		{
			id: FilterOptions.PROVIDER,
			type: 'DROPDOWN',
			filterwhere: 'providerType',
			intlLabelCode: 'provider',
			placeholder: intl.formatMessage(translate('provider')),
			options: PROVIDER_OPTIONS,
		},
		{
			id: FilterOptions.PROCESSED_AT,
			type: 'DATE',
			filterwhere: 'createdAt',
			intlLabelCode: 'processedOn',
		},
		{
			id: FilterOptions.UPDATED_AT,
			type: 'DATE',
			filterwhere: 'eventAt',
			intlLabelCode: 'updatedOn',
		},
		{
			id: FilterOptions.FROM_DATE,
			type: 'DATE',
			filterwhere: 'fromDate',
			intlLabelCode: 'dateFrom',
		},
		{
			id: FilterOptions.TO_DATE,
			type: 'DATE',
			filterwhere: 'toDate',
			intlLabelCode: 'dateTo',
		},
	];

	const fetchEmailTypes = async () => {
		try {
			setIsEmailTypesLoading(true);
			const emailTypes = await DiagnosticsService.getEmailTypes();
			const options = emailTypes.map((item: GetEmailTypesResponseDto, index: number) => ({
				key: index,
				value: item.emailType,
				label: formatEmailTypeLabel(item.emailType),
			}));
			setEmailTypeOptions(options);
		} catch (error) {
			message.error({
				content: intl.formatMessage(translate('commonError')),
				key: 'commonError',
			});
		} finally {
			setIsEmailTypesLoading(false);
		}
	};

	useEffect(() => {
		search({});
		fetchEmailTypes();
	}, []);

	const renderFilter = () => {
		if (isEmailTypesLoading) {
			return <Skeleton.Input active className="email-logs__filter" />;
		}

		return (
			<AdvancedFilter
				showReloadButton={true}
				disabledReloadButton={loading}
				onReload={fetchFilteredData}
				className="email-logs__filter"
				filters={filters}
				onChange={onFilterChange}
				onClearFilters={onClearFilters}
				placeholder={intl.formatMessage(translate('advancedFilter'))}
			/>
		);
	};

	return (
		<div className="view email-logs">
			{renderFilter()}
			<Table
				className="email-logs__table"
				size="small"
				columns={columns}
				dataSource={data}
				loading={loading}
				rowKey="id"
				scroll={{ x: 1500 }}
				pagination={{
					total,
					current: pagination.page,
					pageSize: pagination.size,
					showSizeChanger: true,
					pageSizeOptions: [5, 10, 15, 20],
				}}
				onChange={handleTableChange}
			/>
			{selectedEmailLog && <EventsModal selectedEmailLog={selectedEmailLog} onClose={handleCloseModal} />}
		</div>
	);
};
